import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mic, Star, Building2, Scale, Users, ExternalLink, Clock, Newspaper } from 'lucide-react';
import { SPEAKER_CONFIG, TOPIC_CONFIG, NEWS_SOURCE_COLORS } from '@/lib/constants';

// Icon mapping
const SPEAKER_ICONS = {
  Mic, Star, Building2, Scale, Users
};

/**
 * Video player modal component
 */
export function VideoPlayerModal({ video, onClose }) {
  if (!video) return null;
  
  const speakerConfig = SPEAKER_CONFIG[video.speaker_type] || SPEAKER_CONFIG.influencer;
  const SpeakerIcon = SPEAKER_ICONS[speakerConfig.icon] || Mic;

  return (
    <Dialog open={!!video} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-hidden flex flex-col p-0" data-testid="video-player-modal">
        <div className="aspect-video bg-black">
          <iframe 
            src={video.embed_url} 
            title={video.title} 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen 
            className="w-full h-full" 
          />
        </div>
        <div className="p-4">
          <h3 className="font-bold text-lg mb-2">{video.title}</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <div className={`w-6 h-6 rounded-full ${speakerConfig.color} flex items-center justify-center`}>
              <SpeakerIcon className="h-3 w-3 text-white" />
            </div>
            <span>{video.speaker_name}</span>
          </div>
          <p className="text-sm text-muted-foreground">{video.description}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Article viewer modal component (for Knowledge Hub)
 */
export function ArticleViewerModal({ article, onClose }) {
  if (!article) return null;
  
  const topic = TOPIC_CONFIG[article.category] || TOPIC_CONFIG.guide;

  return (
    <Dialog open={!!article} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-hidden flex flex-col" data-testid="article-viewer-modal">
        <DialogHeader>
          <Badge variant="secondary" className={`w-fit ${topic.color}`}>{topic.label}</Badge>
          <DialogTitle className="text-xl mt-2">{article.title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 min-h-0">
          <div className="bg-muted/50 p-4 rounded-lg mb-4">
            <p className="text-sm text-muted-foreground">{article.summary}</p>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {article.content?.split('\n').map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/**
 * News article viewer modal component
 * Displays news within the portal instead of opening external links
 */
export function NewsViewerModal({ news, onClose }) {
  if (!news) return null;
  
  const sourceColor = NEWS_SOURCE_COLORS[news.source_type] || NEWS_SOURCE_COLORS.newspaper;

  return (
    <Dialog open={!!news} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-hidden flex flex-col" data-testid="news-viewer-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className={sourceColor}>{news.source}</Badge>
            {news.state && <Badge variant="outline">{news.state}</Badge>}
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
              <Clock className="h-3 w-3" />
              {new Date(news.published_at).toLocaleDateString('en-IN', { 
                day: 'numeric', month: 'long', year: 'numeric' 
              })}
            </span>
          </div>
          <DialogTitle className="text-xl mt-3 leading-tight">{news.title}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 min-h-0 mt-4">
          {/* News Summary/Content */}
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {news.summary && (
              <div className="bg-muted/50 p-4 rounded-lg mb-4 not-prose">
                <p className="text-sm text-muted-foreground italic">{news.summary}</p>
              </div>
            )}
            
            {news.content ? (
              news.content.split('\n').map((p, i) => <p key={i}>{p}</p>)
            ) : (
              <div className="text-center py-8">
                <Newspaper className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground mb-4">
                  This is a news headline from {news.source}. 
                  <br />Full article content is available at the source.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
        
        <Separator className="my-4" />
        
        {/* Footer with source link */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Source: {news.source}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.open(news.link, '_blank')}
            className="gap-2"
          >
            Read Full Article <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
